<template>
    <div class="weight-field-operator-popover">
        <div v-if="readonly">
            {{ displayValue }}
        </div>
        <div v-else>
            <template v-if="inlineWhenEmpty && isEmptyValue">
                <WeightFieldDisplayEditOperator
                    ref="editor"
                    :value="value"
                    :unit="unit"
                    :always-edit="true"
                    @input="$emit('input', $event)"
                    @enter="hide"
                />
            </template>
            <template v-else>
                <button
                    :id="targetId"
                    type="button"
                    class="field-link"
                    @click="onToggle"
                >
                    {{ displayValue }}
                </button>
                <b-popover
                    :target="targetId"
                    triggers="click"
                    placement="top"
                    @shown="onShown"
                    @hidden="onHidden"
                >
                    <WeightFieldDisplayEditOperator
                        ref="editor"
                        :value="value"
                        :unit="unit"
                        :always-edit="true"
                        @input="$emit('input', $event)"
                        @enter="hide"
                    />
                </b-popover>
            </template>
        </div>
    </div>
</template>

<script>
import { BPopover } from 'bootstrap-vue'
import WeightFieldDisplayEditOperator from '@/views/product/components/product-card/fmd/common/weight-field/WeightFieldDisplayEditOperator.vue'

export default {
    name: 'WeightFieldOperatorPopover',
    components: {
        BPopover,
        WeightFieldDisplayEditOperator,
    },
    props: {
        value: {
            type: Object,
            default: null,
        },
        editKey: {
            type: String,
            default: '',
        },
        unit: {
            type: String,
            default: '% (w/w)',
        },
        inlineWhenEmpty: {
            type: Boolean,
            default: false,
        },
        autoOpenWhenEmpty: {
            type: Boolean,
            default: false,
        },
        readonly: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            targetId: `weight-op-${this._uid}`,
        }
    },
    mounted() {
        document.addEventListener('mousedown', this.onDocumentClick, true)
        this.maybeAutoOpen()
    },
    beforeDestroy() {
        document.removeEventListener('mousedown', this.onDocumentClick, true)
    },
    computed: {
        emptyText() {
            return this.$t ? this.$t('Component(FMDEditor):set-weight') : 'Set value'
        },
        operatorSymbolMap() {
            return {
                GT: '>',
                GTE: '>=',
                LT: '<',
                LTE: '<=',
            }
        },
        isEmptyValue() {
            const payload = this.value || {}
            const from = payload.from !== undefined ? payload.from : null
            const to = payload.to !== undefined ? payload.to : null
            return from === null && to === null
        },
        displayValue() {
            const payload = this.value || {}
            const from = payload.from !== undefined ? payload.from : null
            const to = payload.to !== undefined ? payload.to : null
            const fromOp = this.operatorSymbolMap[String(payload.fromOperator || 'GTE').toUpperCase()] || '>='
            const toOp = this.operatorSymbolMap[String(payload.toOperator || 'LTE').toUpperCase()] || '<='
            const unit = payload.unit || this.unit
            if (from === null && to === null) {
                return this.emptyText
            }
            if (from !== null && to !== null) {
                if (from === to) {
                    return `${fromOp} ${from} ${unit}`
                }
                return `${fromOp} ${from} - ${toOp} ${to} ${unit}`
            }
            if (from !== null) {
                return `${fromOp} ${from} ${unit}`
            }
            return `${toOp} ${to} ${unit}`
        },
    },
    methods: {
        maybeAutoOpen() {
            if (this.readonly || this.inlineWhenEmpty || !this.autoOpenWhenEmpty || !this.isEmptyValue) {
                return
            }
            this.$nextTick(() => {
                setTimeout(() => {
                    this.$root.$emit('bv::show::popover', this.targetId)
                }, 0)
            })
        },
        emitEditing(isEditing) {
            this.$emit('editing', {
                key: this.editKey,
                isEditing,
            })
        },
        onDocumentClick(event) {
            if (this.readonly) {
                return
            }
            if (this.inlineWhenEmpty && this.isEmptyValue) {
                return
            }
            const target = event && event.target
            if (!target) {
                return
            }
            if (this.$el && this.$el.contains(target)) {
                return
            }
            if (target.closest && target.closest('.popover')) {
                return
            }
            this.hide()
        },
        onToggle() {
            // handled by popover trigger
        },
        onShown() {
            const editor = this.$refs.editor
            if (editor && typeof editor.openEditor === 'function') {
                editor.openEditor()
            }
            this.emitEditing(true)
        },
        onHidden() {
            this.emitEditing(false)
        },
        hide() {
            if (this.inlineWhenEmpty && this.isEmptyValue) {
                return
            }
            this.emitEditing(false)
            this.$root.$emit('bv::hide::popover', this.targetId)
            const el = this.$el.querySelector(`#${this.targetId}`)
            if (el && typeof el.blur === 'function') {
                el.blur()
            }
        },
    },
    watch: {
        value: {
            deep: true,
            handler() {
                this.maybeAutoOpen()
            },
        },
    },
}
</script>

<style scoped>
.field-link {
    background: transparent;
    border: 0;
    padding: 0;
    color: #2b6cb0;
    text-decoration: underline;
    cursor: pointer;
    text-align: left;
}
.field-link:hover {
    color: #1e4e8c;
}
</style>
